import { useTranslation } from '@/i18n';
import { useScrollReveal } from './useScrollReveal';

const steps = [
  {
    num: 1,
    titleKey: 'landing.howItWorks.step1Title',
    descKey: 'landing.howItWorks.step1Desc',
    icon: (
      <svg className="w-7 h-7 text-sky-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    num: 2,
    titleKey: 'landing.howItWorks.step2Title',
    descKey: 'landing.howItWorks.step2Desc',
    icon: (
      <svg className="w-7 h-7 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
  },
  {
    num: 3,
    titleKey: 'landing.howItWorks.step3Title',
    descKey: 'landing.howItWorks.step3Desc',
    icon: (
      <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
];

export function HowItWorks() {
  const { t } = useTranslation();
  const sectionRef = useScrollReveal();

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="landing-reveal py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            {t('landing.howItWorks.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto">
            {t('landing.howItWorks.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-6 relative">
          {/* Connecting arrows (desktop only) */}
          <div className="hidden md:block absolute top-16 left-[calc(33.33%-12px)] w-[calc(33.33%+24px)] h-0.5">
            <div className="w-full h-full bg-gradient-to-r from-sky-200 via-indigo-200 to-green-200" />
          </div>
          <div className="hidden md:block absolute top-16 right-[calc(33.33%-12px)] w-[calc(33.33%+24px)] h-0.5">
            <div className="w-full h-full bg-gradient-to-r from-indigo-200 to-green-200" />
          </div>

          {steps.map((step, i) => (
            <div key={step.num} className="relative text-center">
              {/* Mobile connecting arrow */}
              {i < steps.length - 1 && (
                <div className="md:hidden flex justify-center my-4">
                  <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              )}

              {/* Step number circle */}
              <div className="relative z-10 mx-auto w-14 h-14 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center mb-6">
                {step.icon}
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t(step.titleKey)}
              </h3>
              <p className="text-gray-500 leading-relaxed max-w-sm mx-auto">
                {t(step.descKey)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
